import { useContext, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import _ from 'lodash';
import { booleanPointInPolygon, point as turfPoint } from '@turf/turf';
import {
  mapLayers,
  TILESET_RASTER_OPACITY,
  TILESET_RASTER_RESAMPLING,
} from '../../constants/map';
import { SUITABILITY_LAYER_ID } from '../right-menu/SuitabilityMenu';
import { useTilesetContext } from '../../contexts/TilesetContext';
import { MapContext } from '../../contexts/MapContext';
import colorForLayerPoint from '../../utils/webgl/suitability-calculations/colorForLayerPoint';
import { TILESET_MAX_ZOOM, TILESET_MIN_ZOOM } from '../../constants/tilesets';
import { useUserRole } from '../../utils/hooks';
import { CLIMATE_LAYER_ID } from '../right-menu/ClimateLayerMenu';
import { LAND_USE_LAYER_ID } from '../right-menu/LandUseLayerMenu';
import RasterLayerWithClipping from '../../utils/webgl/RasterLayerWithClipping';

const SOURCE_ID = 'raster-tiles';
const LAYER_ID = 'simple-tiles';
const CLIPPED_LAYER_ID = 'clipped-tiles';

const TILESET_LAYER_IDS = [
  CLIMATE_LAYER_ID,
  LAND_USE_LAYER_ID,
  SUITABILITY_LAYER_ID,
];

const TilesetLayer = ({ map }) => {
  const { isAuthenticated } = useUserRole();
  const {
    tileset,
    tilesetMasked,
    tilesetShown,
    setHoveredColor,
    setTilesetMasked,
  } = useTilesetContext();

  const {
    layers: { layers },
    mapState: { activeLayerId },
  } = useContext(MapContext);
  const boundaryLayerFeatures = useMemo(
    () => layers.BOUNDARY?.data?.features ?? [],
    [layers.BOUNDARY],
  );
  const activeLayerIdRef = useLatest(activeLayerId);
  const tilesetMaskedRef = useLatest(tilesetMasked);
  const boundaryFeaturesRef = useLatest(boundaryLayerFeatures);
  const [clippedTilesLayer, setClippedTilesLayer] = useState(null);

  useEffect(() => {
    const shouldTrackHoveredColor = () =>
      TILESET_LAYER_IDS.includes(activeLayerIdRef.current);
    if (tileset != null && tilesetShown) {
      const tilesetApi = `${window.location.origin}${
        // If unauthenticated, use public endpoint
        isAuthenticated ? '' : '/api/public'
      }/tileset/${tileset.id}/{z}/{x}/{y}.png`;

      map.addSource(SOURCE_ID, {
        type: 'raster',
        tiles: [tilesetApi],
        tileSize: 256,
        minzoom: tileset.zoomLevels
          ? Math.min(...tileset.zoomLevels)
          : TILESET_MIN_ZOOM,
        maxzoom: tileset.zoomLevels
          ? Math.max(...tileset.zoomLevels)
          : TILESET_MAX_ZOOM,
        scheme: 'tms',
      });
      map.addLayer(
        {
          id: LAYER_ID,
          type: 'raster',
          source: SOURCE_ID,
          paint: {
            'raster-opacity': tilesetMasked ? 0 : TILESET_RASTER_OPACITY,
            'raster-resampling': TILESET_RASTER_RESAMPLING,
          },
        },
        mapLayers.TILESET,
      );

      const clippedTilesetLayer = new RasterLayerWithClipping({
        id: CLIPPED_LAYER_ID,
        spec: {
          type: 'raster',
          tiles: [tilesetApi],
          tileSize: 256,
          minzoom: tileset.zoomLevels
            ? Math.min(...tileset.zoomLevels)
            : TILESET_MIN_ZOOM,
          maxzoom: tileset.zoomLevels
            ? Math.max(...tileset.zoomLevels)
            : TILESET_MAX_ZOOM,
          scheme: 'tms',
        },
        maskPolygons: boundaryLayerFeatures,
        opacity: tilesetMasked ? TILESET_RASTER_OPACITY : 0,
        resampling: TILESET_RASTER_RESAMPLING,
      });
      map.addLayer(clippedTilesetLayer, mapLayers.TILESET);
      setClippedTilesLayer(clippedTilesetLayer);

      // Throttle this to avoid performance issues, and throttle both event types via 1 function to avoid race conditions.
      const handleMouseEvent = _.throttle(({ type, point, lngLat }) => {
        if (!shouldTrackHoveredColor()) return;

        if (type === 'mouseout') {
          setHoveredColor(null);
        } else if (type === 'mousemove') {
          const coords = turfPoint([lngLat.lng, lngLat.lat]);
          const pointInTileset =
            !tilesetMaskedRef.current ||
            boundaryFeaturesRef.current.some((f) =>
              booleanPointInPolygon(coords, f),
            );
          if (pointInTileset) {
            setHoveredColor(
              colorForLayerPoint(map, LAYER_ID, point, {
                originalOpacity: tilesetMaskedRef.current
                  ? 0
                  : TILESET_RASTER_OPACITY,
              }),
            );
          } else {
            setHoveredColor(null);
          }
        }
      }, 50);

      const handleMousemove = ({ point, lngLat }) =>
        handleMouseEvent({ type: 'mousemove', point, lngLat });
      const handleMouseout = () => handleMouseEvent({ type: 'mouseout' });

      map.on('mousemove', handleMousemove);
      map.on('mouseout', handleMouseout);

      return () => {
        // Remove layers if map style is not null
        if (map.getStyle()) {
          map.removeLayer(LAYER_ID);
          map.removeSource(SOURCE_ID);
          map.removeLayer(CLIPPED_LAYER_ID);
        }

        map.off('mousemove', handleMousemove);
        map.off('mouseout', handleMouseout);

        setClippedTilesLayer(null);
      };
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileset, tilesetShown]);

  useEffect(() => {
    if (!map?.getLayer(LAYER_ID)) return;
    if (tilesetMasked) {
      map.setPaintProperty(LAYER_ID, 'raster-opacity', 0);
      clippedTilesLayer?.updateOpacity(TILESET_RASTER_OPACITY);
    } else {
      map.setPaintProperty(LAYER_ID, 'raster-opacity', TILESET_RASTER_OPACITY);
      clippedTilesLayer?.updateOpacity(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tilesetMasked]);

  useEffect(() => {
    clippedTilesLayer?.updateMask(boundaryLayerFeatures ?? []);
    // Reset tileset masked state if no boundary features
    if (!boundaryLayerFeatures?.length) {
      setTilesetMasked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryLayerFeatures]);

  return null;
};

export default TilesetLayer;
